<template>
    <div class="cs_formgrid" :class="{ readonly: !this.editing }">
    <template v-for="field in componentData.fields.filter(a => !a?.state || (a?.state === 'Online'))" :key="field.id">
        <div v-if="isFieldVisible(resolveField(field))" :class="getClassesByField(resolveField(field))">
            <div class="FieldContent" v-for="item in resolveContent(field)">
                <div class="h3" v-if="item?.title">{{ trans(item?.title) }}</div>
                <div class="Text" v-if="item?.text">{{ trans(item?.text) }}</div>
            </div>
        <component
            :is="resolveType(field)"
            v-bind="resolveField(field)"
            :field="resolveField(field)"
            :value="value(resolveField(field))"
            :values="this.values"
            :module_object="this.module_object"
            :editing="resolveEditing(field)"
            :preview="resolvePreview(field)"
            :extra="resolveExtra(field)"
            :prefilter="this.prefilter"
        >
            <div class="element TextField">
                <label>{{ field.label }}</label>
                <small>{{ field.type }} isn't available yet</small>
            </div>
        </component>
        </div>
    </template>
    </div>
</template>

<script>
import { reactive, defineComponent, watch, computed, provide, inject, onUnmounted } from "vue";
import { mergeDeep } from "helpers/_objects";

import { env } from "config";
import { company } from "store/company";
import trans from "helpers/_translation";

import { lower_roles, rolePropertyNames } from "store/portal";

// Our components
import Elements from 'components/elements/_index';

export default defineComponent({
    name: "fields",
    props: {
        fields: {
            type: Array,
            required: true
        },
        values: {
            type: Object,
            required: true
        },
        module_object: {
            type: String,
            required: false,
            default: ""
        },
        prefill: {
            type: Boolean,
            required: false,
            default: true
        },
        prefilter: {
            type: Object,
            required: false,
            default: true
        },
        // This boolean allows us too set all fields to readonly at start and only allow editing when requested
        editing: {
            type: Boolean,
            required: false,
            default: true
        },
        preview: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    components: {
        ...Elements
    },
    setup(props) {
        const viewData = inject("viewData");
        const componentData = reactive({
            fields: []
        });

        const filterOptionJsonMap = {
            "role": (options) => {
                return Object.keys(options).filter(a => {
                    return lower_roles.value.includes(a);
                }).reduce((obj, key) => {
                    obj[key] = options[key];
                    return obj;
                }, {});
            }
        };
        const filterOptionJson = (field) => {
            let optionObject = ((field?.options && Object.values(field.options).length) ? {...field.options} : JSON.parse(field.options_json || "[]"));
            const customElement = overruledSettings(field);
            const customFilters = customElement?.field?.option_filters ?? [];

            if (customFilters.length) {
                customFilters.forEach(key => {
                    if (!(key in filterOptionJsonMap)) {
                        console.log("Unknown filter key", key);
                        return;
                    }
                    optionObject = filterOptionJsonMap[key](optionObject);
                });
            }
            if (rolePropertyNames.value.includes(field.name)) {
                optionObject = filterOptionJsonMap["role"](optionObject);
            }

            return optionObject;
        };

        const fieldWatchers = [];
        const formatFieldData = (fields = []) => {
            componentData.fields = [];
            fields.forEach(field => {
                const bools = [
                    "auto_fill",
                    "multiple_languages",
                    "readonly",
                    "validate",
                    "float"
                ];
                // Reformat our booleans, making sure we get true or false
                bools.forEach(key => {
                    field[key] = (typeof field[key] === "boolean") ? field[key] : (field[key] === "Y");
                });

                // Make sure validation is passed
                if (!field?.required && ("validate" in field)) {
                    field.required = field.validate;
                }
                if (!field?.float && field?.extra?.["newline"]) {
                    field.float = !field.extra.newline;
                }

                // Check the field name, if it matched the current env("company_property_name")
                // We make this a hidden field instead with correct value preset
                const companyProperties = env("company_property_prefilled");
                if (props.prefill && companyProperties.includes(field.name)) {
                    const defaultValue = computed(() => {
                        if (`${viewData.item?.data?.data?.workflowcardtype_id}` === `${env('variables.company.workflowcardtype_id')}`) {
                            return viewData.workflowcard_id;
                        } else {
                            return company.value?.data?.id;
                        }
                    });
                    fieldWatchers.push(watch(
                        () => defaultValue,
                        () => {
                            if (!defaultValue.value) return;
                            if (!field.type.endsWith("MultiCardField")) {
                                field.type = "HiddenField";
                            }
                            field.default_value = defaultValue.value;
                        },
                        {
                            immediate: true,
                            deep: true
                        }
                    ));
                }

                // Hook into the options_json, filter out items which should be filtered
                field.options = filterOptionJson(field);
                if (field.type === "SelectField") {
                    // For now only set for SelectField since not sure of impact on other fields
                    field.options_json = JSON.stringify(field.options);
                }

                componentData.fields.push(field);
            });
        };

        const customElementMap = env("custom_element_mapping");
        const overruledSettings = (field) => {
            return computed(() => {
                if (!customElementMap) {
                    return null;
                }
                return customElementMap.find(a => {
                    return (`${a.workflowpropertyfield_id}` === `${field.id}`) && (`${a.workflowboard_id}` === `${viewData.workflowboard_id}`);
                }) ?? null;
            }).value;
        };

        const resolveContent = (field) => {
            return computed(() => {
                const customElement = overruledSettings(field);
                return customElement?.field?.content ?? [];
            }).value;
        };
        const resolveOptionContent = (field, key) => {
            return computed(() => {
                const customElement = overruledSettings(field);
                const customConfiguration = customElement?.field?.option_content ?? [];
                return customConfiguration.find(a => a.key === key);
            }).value;
        };
        provide("resolveOptionContent",resolveOptionContent);

        const resolveType = (field) => {
            return computed(() => {
                const customElement = overruledSettings(field);
                return customElement?.field?.component ?? field?.type.split("\\").pop() ?? "";
            }).value;
        };
        const resolveEditing = (field) => {
            return computed(() => {
                const customElement = overruledSettings(field);
                return customElement?.field?.editing ?? props.editing;
            }).value;
        };
        const resolvePreview = (field) => {
            return computed(() => {
                const customElement = overruledSettings(field);
                return customElement?.field?.preview ?? props.preview;
            }).value;
        };
        const resolveExtra = (field) => {
            return computed(() => {
                const customElement = overruledSettings(field);
                return customElement?.field?.extra || field.extra;
            }).value;
        };
        const resolveField = (field) => {
            return computed(() => {

                const tmpSettings = JSON.parse(JSON.stringify(overruledSettings(field)));
                if (tmpSettings?.field) {
                    ["extra", "type", "preview", "editing"].forEach(key => {
                        if (key in tmpSettings.field) {
                            delete tmpSettings.field[key];
                        }
                    });
                }

                return mergeDeep(field, tmpSettings?.field ?? {});
            }).value;
        }

        watch(
            () => props.fields.length,
            () => {
                formatFieldData(props.fields)
            },
            {
                immediate: true
            }
        );

        const getClassesByField = (field) => {
            const extra = resolveExtra(field);

            let colClass = extra?.colclass || "span12";
            let rowClass = extra?.rowclass || "row1";

            if (!extra?.colclass && (resolveType(field) === "EditorField")) {
                colClass = "span12";
            }
            if (resolveType(field) === "HiddenField") {
                colClass = "span12 hidden";
            }

            return [ colClass, rowClass ];
        };
        const value = (field) => {
            return computed(() => {
                // If prefill false and field is one of the company properties, return undefined to prevent auto filling
                if (!props.prefill) {
                    return null;
                }
                return props.values?.value?.[`${field.id}`]?.value ?? props.values?.[`${field.id}`]?.value ?? field.default_value;
            }).value;
        };

        const isFieldVisible = (field) => {
            return computed(() => {
                // When editing, always show
                if (props.editing || field.type.endsWith("CardField")) {
                    return true;
                }
                // Check for value, if value, show
                const fieldValue = props.values.value[`${field.id}`]?.value ?? field.default_value;
                if (!resolvePreview(field) || fieldValue) {
                    return true;
                }
                // Default is false
                return false;
            }).value;
        };

        onUnmounted(() => {
            fieldWatchers.forEach(watcher => {
                watcher && watcher();
            })
        });

        return {
            trans,

            componentData,
            getClassesByField,
            value,
            isFieldVisible,

            resolveType,
            resolveContent,
            resolveEditing,
            resolvePreview,
            resolveExtra,
            resolveField
        };
    },
});
</script>

<style></style>